import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUndo, faCheckCircle, faPlus, faMinus, faChevronDown, faChevronRight, 
  faInfoCircle, faCircleNotch, faTruckMoving, faClock
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';
import { useCalculatorData } from './useCalculatorData';

const goldColor = '#c5a059';

const MachineryCalculator = ({ onResultChange }) => {
  const { t } = useTranslation();
  const { meta, loading: metaLoading } = useCalculatorData(6);

  const [step, setStep] = useState(0); 
  const [openId, setOpenId] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [resultData, setResultData] = useState({ total: 0, summary: [], details: "" });
  const [selectedMachinery, setSelectedMachinery] = useState({});

  const machineryMenu = useMemo(() => {
    if (!meta.assets || meta.assets.length === 0) return [];

    return meta.assets
        .filter(asset => {
            const assetId = Number(asset.id);
            return assetId >= 6000 && assetId <= 6999;
        })
        .map(asset => {
            let opts = {};
            try {
                opts = typeof asset.options === 'string' ? JSON.parse(asset.options || '{}') : (asset.options || {});
            } catch (e) { opts = {}; }

            return {
                id: asset.id,
                name: asset.name,
                min: opts.min_hours || opts.min || 2,
                desc: asset.description || asset.name,
                icon: faTruckMoving 
            };
        });
  }, [meta.assets]);

  const updateHours = (e, item, change) => {
    e.stopPropagation();
    setSelectedMachinery(prev => {
      const currentHours = prev[item.id] || 0;
      let newHours;
      if (currentHours === 0 && change > 0) newHours = item.min;
      else newHours = currentHours + change;

      if (newHours < item.min && change < 0) newHours = 0;
      
      const newState = { ...prev };
      if (newHours === 0) delete newState[item.id];
      else newState[item.id] = newHours;
      return newState;
    });
  };

  const calculate = async () => {
    if (Object.keys(selectedMachinery).length === 0) return;
    setCalcLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/api/calculators/calculate`, {
        category: 'MACHINERY',
        data: { selectedMachinery } 
      });

      if (response.data.success) {
        const { total, summary, business } = response.data; 
        const finalDetailsText = `${t('machinery_label')}: ${summary}`;

        const orderDataForBackend = {
          total_price: Number(total),
          truck_price: Number(total),
          truck_quantity: business?.truckQuantity || 1,
          details: finalDetailsText,
          category: 'MACHINERY',
          requested_vehicles: business?.vehicleSlots || [], 
          business: business, 
          items_json: JSON.stringify(summary)
        };

        setResultData({
          total: total,
          summary: Array.isArray(summary) ? summary : [summary],
          details: finalDetailsText
        });

        if (onResultChange) {
          onResultChange(total, finalDetailsText, orderDataForBackend);
        }
        setStep(1);
      }
    } catch (error) {
      alert(t('calculation_error'));
    } finally {
      setCalcLoading(false);
    }
  };

  const handleEdit = () => {
    setStep(0);
    if (onResultChange) onResultChange(0, "");
  };

  if (metaLoading) return (
    <div style={{...s.container, display:'flex', alignItems:'center', justifyContent:'center', minHeight:'300px'}}>
      <FontAwesomeIcon icon={faCircleNotch} spin size="2x" color={goldColor} />
    </div>
  );

  return (
    <div style={s.container}>
      {step === 0 ? (
        <div style={s.flexBody}>
          <div style={s.headerInfo}>
            <div style={s.iconBadge}><FontAwesomeIcon icon={faTruckMoving} /></div>
            <div>
              <div style={s.mainTitle}>{t('machinery_title')}</div>
              <div style={s.subtitle}>{t('machinery_subtitle')}</div>
            </div>
          </div>

          <div style={s.itemsContainer}>
            {machineryMenu.map(item => {
              const isOpen = openId === item.id;
              const hasHours = (selectedMachinery[item.id] || 0) > 0;

              return (
                <div key={item.id} style={{
                  ...s.groupCard,
                  borderColor: hasHours ? goldColor : '#e2e8f0',
                  boxShadow: hasHours ? `0 4px 12px ${goldColor}22` : 'none'
                }}>
                  <div style={s.accordionHeader} onClick={() => setOpenId(isOpen ? null : item.id)}>
                    <div style={s.headerLeft}>
                      <div style={{...s.itemIcon, color: hasHours ? goldColor : '#94a3b8'}}><FontAwesomeIcon icon={item.icon} /></div>
                      <div>
                        <div style={{...s.itemName, color: hasHours ? '#0f172a' : '#475569'}}>{item.name}</div>
                        <div style={s.minText}>{t('min_hours', { count: item.min })}</div>
                      </div>
                    </div>
                    <div style={s.headerRight}>
                      {hasHours && <div style={s.selectedBadge}>{t('selected_hours', { count: selectedMachinery[item.id] })}</div>}
                      <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} size="xs" color={goldColor} />
                    </div>
                  </div>

                  {isOpen && (
                    <div style={s.accordionContent}>
                      <div style={s.infoBox}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{color: goldColor}} />
                        <span style={s.itemDesc}>{item.desc}</span>
                      </div>
                      <div style={s.counterRow}>
                        <div style={s.hourLabel}><FontAwesomeIcon icon={faClock} style={{marginRight: '6px'}} /> {t('hour_quantity')}</div>
                        <div style={s.counter}>
                          <button style={s.qtyBtn} onClick={(e) => updateHours(e, item, -1)}><FontAwesomeIcon icon={faMinus} size="xs"/></button>
                          <span style={s.qtyInput}>{selectedMachinery[item.id] || 0}</span>
                          <button style={s.qtyBtn} onClick={(e) => updateHours(e, item, 1)}><FontAwesomeIcon icon={faPlus} size="xs"/></button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button 
            style={{...s.mainBtn, background: Object.keys(selectedMachinery).length === 0 ? '#cbd5e1' : '#0f172a'}} 
            onClick={calculate} 
            disabled={calcLoading || Object.keys(selectedMachinery).length === 0}
          >
            {calcLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('calculate_btn')}
          </button>
        </div>
      ) : (
        <div style={s.successCard}>
          <div style={s.successIcon}><FontAwesomeIcon icon={faCheckCircle} size="3x" color="#10b981" /></div>
          <h3 style={s.successTitle}>{t('calculation_ready')}</h3>
          <div style={s.receiptCard}>
              <div style={s.receiptLabel}>{t('order_summary')}</div>
              <div style={s.itemsSummary}>
                {resultData.summary && resultData.summary.map((line, idx) => (
                  <div key={idx} style={s.summaryLine}>• {line}</div>
                ))}
              </div>
              <div style={s.receiptDivider} />
              <div style={s.finalRow}>
                <span>{t('total')}</span>
                <span style={s.finalPrice}>{(resultData.total || 0).toLocaleString()} ֏</span>
              </div>
          </div>
          <button style={s.secondaryBtn} onClick={handleEdit}>
            <FontAwesomeIcon icon={faUndo} style={{marginRight: '8px'}} /> {t('modify_btn')}
          </button>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { width: '100%', maxWidth: '500px', margin: '0 auto', padding: '15px', boxSizing: 'border-box' },
  flexBody: { display: 'flex', flexDirection: 'column' },
  headerInfo: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' },
  iconBadge: { width: '45px', height: '45px', background: '#fffcf5', border: `1px solid ${goldColor}44`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: goldColor, fontSize: '20px' },
  mainTitle: { fontSize: '18px', fontWeight: '800', color: '#0f172a' },
  subtitle: { color: '#64748b', fontSize: '12px', fontWeight: '500' },
  itemsContainer: { maxHeight: '500px', overflowY: 'auto', paddingRight: '4px' },
  groupCard: { marginBottom: '12px', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden', transition: 'all 0.3s' },
  accordionHeader: { padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  itemIcon: { fontSize: '18px', width: '24px', textAlign: 'center' },
  itemName: { fontWeight: '700', fontSize: '15px' },
  minText: { fontSize: '10px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' },
  selectedBadge: { background: goldColor, color: '#fff', fontSize: '11px', padding: '4px 10px', borderRadius: '20px', fontWeight: '800' },
  accordionContent: { padding: '16px', borderTop: '1px solid #f8fafc', background: '#fafbfc' },
  infoBox: { display: 'flex', gap: '8px', alignItems: 'center', background: '#fff', padding: '10px', borderRadius: '10px', marginBottom: '15px', border: '1px solid #f1f5f9' },
  itemDesc: { fontSize: '11px', color: '#475569' },
  counterRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  hourLabel: { fontSize: '13px', fontWeight: '700', color: '#1e293b' },
  counter: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '10px', border: `1px solid ${goldColor}44`, padding: '4px' },
  qtyBtn: { border: 'none', background: 'none', color: goldColor, width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  qtyInput: { minWidth: '40px', textAlign: 'center', fontSize: '14px', fontWeight: '800' },
  mainBtn: { width: '100%', padding: '18px', borderRadius: '16px', border: 'none', color: '#fff', fontWeight: '800', cursor: 'pointer', marginTop: '15px' },
  successCard: { textAlign: 'center' },
  successIcon: { marginBottom: '15px' },
  successTitle: { fontSize: '20px', fontWeight: '900', color: '#0f172a', marginBottom: '20px' },
  receiptCard: { background: '#fff', padding: '24px', borderRadius: '20px', border: '1px solid #e2e8f0', textAlign: 'left', marginBottom: '20px' },
  receiptLabel: { color: goldColor, fontWeight: '800', fontSize: '11px', marginBottom: '15px' },
  itemsSummary: { display: 'flex', flexDirection: 'column', gap: '8px' },
  summaryLine: { fontSize: '14px', color: '#334155', fontWeight: '500' },
  receiptDivider: { height: '1px', borderTop: '1px dashed #e2e8f0', margin: '20px 0' },
  finalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  finalPrice: { fontSize: '22px', fontWeight: '900', color: '#0f172a' },
  secondaryBtn: { width: '100%', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: '700', cursor: 'pointer' }
};

export default MachineryCalculator;